import React, { useCallback, useLayoutEffect, useState } from 'react';
import { MuiMaterials } from '../@index.js';
import {
    ModalChrome,
    ModalBody,
    ModalContentTile,
    SiteSelectPhase,
    SITE_WINDOW_IDS,
    comparisonHeaderButtonSx,
    containedActionButtonSx,
} from './windowChrome/index.js';
import ReportTemperatureComparisonWindow from './ReportTemperatureComparisonWindow';
import ReportWindow from './ReportWindow';

const SiteComparisonModal = ({ isModalOpen, setIsModalOpen, initialSiteGuid = null }) => {
    const [pickerSiteGuid, setPickerSiteGuid] = useState('');
    const [selectedSiteGuid, setSelectedSiteGuid] = useState('');
    const [formActions, setFormActions] = useState(null);
    const [historyActions, setHistoryActions] = useState(null);
    const [showHistory, setShowHistory] = useState(false);

    // Apply carried siteGuid before paint so tool-switching does not flash the picker.
    useLayoutEffect(() => {
        if (!isModalOpen[0]) {
            setPickerSiteGuid('');
            setSelectedSiteGuid('');
            setFormActions(null);
            setHistoryActions(null);
            setShowHistory(false);
            return;
        }
        if (initialSiteGuid) {
            setPickerSiteGuid(initialSiteGuid);
            setSelectedSiteGuid(initialSiteGuid);
        }
    }, [isModalOpen, initialSiteGuid]);

    const handleClose = () => setIsModalOpen([false]);
    const handleActionsChange = useCallback((next) => setFormActions(next), []);
    const handleHistoryActionsChange = useCallback((next) => setHistoryActions(next), []);

    const activeSiteGuid =
        selectedSiteGuid || (isModalOpen[0] ? initialSiteGuid : '') || '';
    const phase = activeSiteGuid ? 'edit' : 'select';
    const activeActions = showHistory ? historyActions : formActions;

    return (
        <ModalChrome
            open={isModalOpen[0]}
            onClose={handleClose}
            title="Site Comparison"
            toolTitle="Comparison"
            siteWindowId={SITE_WINDOW_IDS.comparison}
            siteGuid={activeSiteGuid || null}
            showHeaderSiteSelect={phase === 'edit'}
            onHeaderSiteChange={(guid) => {
                setPickerSiteGuid(guid);
                setSelectedSiteGuid(guid);
                setFormActions(null);
                setHistoryActions(null);
                setShowHistory(false);
            }}
            headerFilterSite={(s) => !s.isHms}
            actions={
                phase === 'edit' ? (
                    <MuiMaterials.Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        <MuiMaterials.Tooltip
                            title="Toggles a calendar display of comparison reports that were processed via GrainWave."
                            arrow
                            placement="bottom"
                        >
                            <MuiMaterials.Button
                                size="small"
                                variant={showHistory ? 'contained' : 'outlined'}
                                onClick={() => setShowHistory((v) => !v)}
                                sx={showHistory ? containedActionButtonSx() : comparisonHeaderButtonSx()}
                            >
                                History
                            </MuiMaterials.Button>
                        </MuiMaterials.Tooltip>
                        <MuiMaterials.LoadingButton
                            size="small"
                            variant="outlined"
                            loading={!!activeActions?.loadingReport}
                            disabled={!activeActions?.onView}
                            onClick={() => activeActions?.onView?.()}
                            sx={comparisonHeaderButtonSx()}
                        >
                            View
                        </MuiMaterials.LoadingButton>
                        <MuiMaterials.LoadingButton
                            size="small"
                            variant="outlined"
                            loading={!!(activeActions?.loadingEmail || (showHistory && activeActions?.loadingReport))}
                            disabled={!activeActions?.onEmail}
                            onClick={() => activeActions?.onEmail?.()}
                            sx={comparisonHeaderButtonSx()}
                        >
                            Email
                        </MuiMaterials.LoadingButton>
                    </MuiMaterials.Box>
                ) : null
            }
            className="siteComparisonModal hide-scrollbar"
        >
            {phase === 'select' ? (
                <SiteSelectPhase
                    value={pickerSiteGuid}
                    onChange={setPickerSiteGuid}
                    onContinue={(guid) => {
                        setPickerSiteGuid(guid);
                        setSelectedSiteGuid(guid);
                    }}
                    onCancel={handleClose}
                    filterSite={(s) => !s.isHms}
                />
            ) : (
                <ModalBody className="hide-scrollbar">
                    <ModalContentTile
                        className="hide-scrollbar"
                        sx={{
                            overflowX: 'hidden',
                            overflowY: 'auto',
                            overscrollBehavior: 'contain',
                            WebkitOverflowScrolling: 'touch',
                            position: 'relative',
                            // Keep outlined field labels from clipping at the tile top edge.
                            pt: showHistory ? 2.5 : 2,
                        }}
                    >
                        {showHistory ? (
                            <ReportWindow
                                key={`comparison-history-${activeSiteGuid}`}
                                variant="modal"
                                lockedSiteGuid={activeSiteGuid}
                                hideFooterActions
                                onActionsChange={handleHistoryActionsChange}
                            />
                        ) : (
                            <ReportTemperatureComparisonWindow
                                variant="modal"
                                lockedSiteGuid={activeSiteGuid}
                                hideFooterActions
                                onActionsChange={handleActionsChange}
                            />
                        )}
                    </ModalContentTile>
                </ModalBody>
            )}
        </ModalChrome>
    );
};

export default SiteComparisonModal;
